import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { Button, Tooltip, ListItemText, Stack } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import axiosInstance from 'src/utils/axios';
import { fAPIDate } from 'src/utils/format-time';
import { TAB_QUERY } from 'src/utils/common-types';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteCheque,
  getChequeReceived,
  getCheques,
  getChequeTotal,
  removeChequeItem,
} from 'src/redux/slices/cheque';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  CHEQUE_STATUS_OPTIONS,
  ChequeStatus,
  IChequeListItem,
  IChequeReceived,
  IChequeTableFilters,
  IChequeTableFilterValue,
  IChequeTableSort,
} from 'src/types/cheque';

import ChequeTableRow from '../cheque-table-row';
import ChequeTableToolbar from '../cheque-table-toolbar';

const STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...CHEQUE_STATUS_OPTIONS];

const TABLE_HEAD = [
  { id: 'chequeNumber', label: 'Cheque Number', align: 'left', primary: true },
  { id: 'customer', label: 'Customer', align: 'left', primary: true },
  { id: 'holder', label: 'Holder', align: 'left', primary: true },
  { id: 'chequeDate', label: 'Cheque Date', align: 'left', primary: true },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'status', label: 'Status', align: 'left', primary: true },
  { id: 'receivedDate', label: 'Received At', align: 'left', primary: true },
  { id: '', primary: true },
];

function getDynamicTableHead(
  filters: IChequeTableFilters,
  isPaidTab: boolean,
  isBankingTab: boolean
) {
  // Create a copy to avoid mutating the original array
  const updatedTableHead = [...TABLE_HEAD];

  // The Paid tab shows when the cheque was paid instead of when it was received.
  if (isPaidTab) {
    const receivedIndex = updatedTableHead.findIndex((col) => col.id === 'receivedDate');
    updatedTableHead[receivedIndex] = {
      id: 'paidDate',
      label: 'Paid Date',
      align: 'left',
      primary: true,
    };
  }

  if (isBankingTab) {
    const receivedIndex = updatedTableHead.findIndex((col) => col.id === 'receivedDate');
    updatedTableHead[receivedIndex] = {
      id: 'bankedAt',
      label: 'Banking Date',
      align: 'left',
      primary: true,
    };
  }

  if (filters.paidDate) {
    const statusIndex = updatedTableHead.findIndex((col) => col.id === 'status');
    if (statusIndex !== -1) {
      updatedTableHead.splice(statusIndex + 1, 0, {
        id: 'paidTo',
        label: 'Paid To',
        align: 'left',
        primary: true,
      });
    }
  }

  return updatedTableHead;
}

const defaultFilters: IChequeTableFilters = {
  generalSearch: '',
  accountNumber: null,
  bank: null,
  chequeDate: null,
  receivedVia: null,
  startDate: null,
  endDate: null,
  receivedDate: null,
  chequeDateUpTo: null,
  paidDate: null,
  bankedDate: null,
  type: null,
  isFactored: null,
  factoringStatus: null,
};

const defaultSortBy: IChequeTableSort = {
  sortBy: 'receivedDate',
  order: 'desc',
};

const DEFAULT_ROWS_PER_PAGE = 25;

const SORTABLE_COLUMNS = [...TABLE_HEAD.map(({ id }) => id), 'paidTo', 'paidDate', 'bankedAt'];

// The list state (page, filters, sort) lives in the URL, so coming back from
// a cheque's details page lands on the same page instead of the first.
const readPositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const readDate = (value: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const DATE_FILTER_PARAMS = [
  'chequeDate',
  'startDate',
  'endDate',
  'receivedDate',
  'chequeDateUpTo',
  'paidDate',
  'bankedDate',
] as const;

const TEXT_FILTER_PARAMS = [
  'accountNumber',
  'bank',
  'receivedVia',
  'type',
  'factoringStatus',
] as const;

function readFilters(params: URLSearchParams): IChequeTableFilters {
  const filters: IChequeTableFilters = { ...defaultFilters };

  filters.generalSearch = params.get('search') ?? '';

  TEXT_FILTER_PARAMS.forEach((key) => {
    filters[key] = params.get(key) || null;
  });

  DATE_FILTER_PARAMS.forEach((key) => {
    filters[key] = readDate(params.get(key));
  });

  const isFactored = params.get('isFactored');
  if (isFactored === 'true' || isFactored === 'false') filters.isFactored = isFactored === 'true';

  return filters;
}

export default function ChequeListView() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTab = searchParams.get(TAB_QUERY);

  const { status, cheque, error, total, received } = useAppSelector((state) => state.cheque);

  const chequeTotal: { totalAmount: number | null; pendingWithdrawal: number | null } = total.data;

  const receivedPeople: IChequeReceived[] = received.data;

  const { user: authUser } = useAuthContext();

  const table = useTable({
    defaultRowsPerPage: readPositiveInt(searchParams.get('rowsPerPage'), DEFAULT_ROWS_PER_PAGE),
    defaultCurrentPage: readPositiveInt(searchParams.get('page'), 1) - 1,
    defaultDense: true,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState<IChequeTableFilters>(() => readFilters(searchParams));

  const [sort, setSort] = useState<IChequeTableSort>(() => {
    const sortBy = searchParams.get('sortBy');

    return {
      sortBy: sortBy && SORTABLE_COLUMNS.includes(sortBy) ? sortBy : defaultSortBy.sortBy,
      order:
        searchParams.get('order') === 'asc' || searchParams.get('order') === 'desc'
          ? (searchParams.get('order') as IChequeTableSort['order'])
          : defaultSortBy.order,
    };
  });

  const canReset = !isEqual(defaultFilters, filters);

  const [statusTab, setStatusTab] = useState<string>(currentTab || ChequeStatus.IN_HAND);

  // Filters that sit in the toolbar itself (not the popover) aren't counted.
  // Received Date and Paid Date swap places on the Paid tab.
  const toolbarFilterKeys = ['generalSearch', 'accountNumber', 'chequeDateUpTo'].concat(
    // eslint-disable-next-line no-nested-ternary
    statusTab === ChequeStatus.PAID
      ? ['paidDate']
      : statusTab === ChequeStatus.BANKING
      ? ['bankedDate']
      : ['receivedDate']
  );

  const activeFilterCount = (Object.keys(defaultFilters) as (keyof IChequeTableFilters)[]).filter(
    (key) => !toolbarFilterKeys.includes(key) && !isEqual(filters[key], defaultFilters[key])
  ).length;

  const isPaidTab = statusTab === ChequeStatus.PAID;
  const isBankingTab = statusTab === ChequeStatus.BANKING;

  // Received Date and Paid Date are swapped on the Paid tab, so a sort left on
  // the other one carries over to its counterpart.
  const activeSort: IChequeTableSort = {
    ...sort,
    sortBy: (() => {
      if (isPaidTab && sort.sortBy === 'receivedDate') return 'paidDate';
      if (isBankingTab && sort.sortBy === 'receivedDate') return 'bankedAt';
      if (!isPaidTab && sort.sortBy === 'paidDate') return 'receivedDate';
      if (!isBankingTab && sort.sortBy === 'bankedAt') return 'receivedDate';
      return sort.sortBy;
    })(),
  };

  const handleFilters = useCallback(
    (name: string, value: IChequeTableFilterValue) => {
      table.onResetPage();

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.cheque.edit(id));
    },
    [router]
  );

  const handleFilterStatus = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setStatusTab(newValue);

    // The "passing by" picker only exists on In Hand — don't let its value
    // silently keep filtering another tab.
    if (newValue !== ChequeStatus.IN_HAND) {
      setFilters((prev) => (prev.chequeDateUpTo ? { ...prev, chequeDateUpTo: null } : prev));
    }

    // Paid Date filters All and Paid only; elsewhere it would silently filter.
    if (newValue !== 'all' && newValue !== ChequeStatus.PAID) {
      setFilters((prev) => (prev.paidDate ? { ...prev, paidDate: null } : prev));
    }

    // Banking Date only exists on the Banking tab.
    if (newValue !== ChequeStatus.BANKING) {
      setFilters((prev) => (prev.bankedDate ? { ...prev, bankedDate: null } : prev));
    }
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();

    if (statusTab !== ChequeStatus.IN_HAND) next.set(TAB_QUERY, statusTab);
    if (page > 0) next.set('page', String(page + 1));
    if (rowsPerPage !== DEFAULT_ROWS_PER_PAGE) next.set('rowsPerPage', String(rowsPerPage));
    if (filters.generalSearch) next.set('search', filters.generalSearch);

    TEXT_FILTER_PARAMS.forEach((key) => {
      const value = filters[key];
      if (value) next.set(key, value);
    });

    DATE_FILTER_PARAMS.forEach((key) => {
      const value = filters[key];
      if (value) next.set(key, fAPIDate(value));
    });

    if (filters.isFactored !== null) next.set('isFactored', String(filters.isFactored));
    if (sort.sortBy !== defaultSortBy.sortBy) next.set('sortBy', sort.sortBy);
    if (sort.order !== defaultSortBy.order) next.set('order', sort.order);

    // Replace, so paging around doesn't pile up history entries.
    setSearchParams(next, { replace: true });
  }, [statusTab, page, rowsPerPage, filters, sort, setSearchParams]);

  const fetchPaginatedCheques = useCallback(() => {
    dispatch(
      getChequeTotal({
        generalSearch: filters.generalSearch,
        accountNumber: filters.accountNumber,
        status: statusTab === 'all' ? null : statusTab,
        startDate: filters.startDate,
        endDate: filters.endDate,
        receivedDate: filters.receivedDate,
        receivedVia: filters.receivedVia,
        paidDate: filters.paidDate,
        bankedDate: filters.bankedDate,
        chequeDateUpTo: filters.chequeDateUpTo,
        type: filters.type,
        isFactored: filters.isFactored,
        factoringStatus: filters.factoringStatus,
      })
    );

    dispatch(
      getCheques({
        page,
        limit: rowsPerPage,
        sortBy: activeSort.sortBy,
        sort: activeSort.order,
        generalSearch: filters.generalSearch,
        accountNumber: filters.accountNumber,
        status: statusTab === 'all' ? null : statusTab,
        startDate: filters.startDate,
        endDate: filters.endDate,
        receivedDate: filters.receivedDate,
        receivedVia: filters.receivedVia,
        paidDate: filters.paidDate,
        bankedDate: filters.bankedDate,
        chequeDateUpTo: filters.chequeDateUpTo,
        type: filters.type,
        isFactored: filters.isFactored,
        factoringStatus: filters.factoringStatus,
      })
    );
  }, [dispatch, page, rowsPerPage, activeSort.sortBy, activeSort.order, filters, statusTab]);

  const fetchChequeReceived = useCallback(() => {
    dispatch(getChequeReceived());
  }, [dispatch]);

  const downloadChequeReport = useCallback(async () => {
    // Mirrors the query shape getCheques sends (src/redux/slices/cheque.ts) — the
    // DTO requires every string field present, even empty, or validation fails.
    const params: Record<string, string> = {
      generalSearch: filters.generalSearch ?? '',
      customer: '',
      chequeNumber: '',
      receivedVia: filters.receivedVia ?? '',
      holder: '',
      chequeDate: filters.chequeDate ? fAPIDate(filters.chequeDate) : '',
      receivedDate: filters.receivedDate ? fAPIDate(filters.receivedDate) : '',
      paidDate: filters.paidDate ? fAPIDate(filters.paidDate) : '',
      bankedDate: filters.bankedDate ? fAPIDate(filters.bankedDate) : '',
      bank: filters.bank ?? '',
      status: statusTab === 'all' ? '' : statusTab,
      type: filters.type ?? '',
      sort: activeSort.order,
      sortBy: activeSort.sortBy,
      startDate: filters.startDate ? fAPIDate(filters.startDate) : '',
      endDate: filters.endDate ? fAPIDate(filters.endDate) : '',
    };

    // Optional on the DTO (unlike the fields above) — only send when set.
    if (filters.chequeDateUpTo) params.chequeDateUpTo = fAPIDate(filters.chequeDateUpTo);

    // accountNumber is a numeric field on the DTO — sending it empty fails
    // validation, so it must be left off entirely instead of sent as ''.
    if (filters.accountNumber) params.accountNumber = filters.accountNumber;

    // isFactored is a boolean field on the DTO — same reasoning, only send when set.
    if (filters.isFactored !== null) params.isFactored = String(filters.isFactored);
    if (filters.factoringStatus) params.factoringStatus = filters.factoringStatus;

    try {
      const response = await axiosInstance.get('cheque/download', {
        params,
        responseType: 'blob',
        headers: {
          'Content-Type': 'text/csv',
        },
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'Cheque_List.csv';
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      enqueueSnackbar('Failed to download cheque report', { variant: 'error' });
    }
  }, [filters, activeSort.sortBy, activeSort.order, statusTab]);

  const handleDeleteRow = useCallback(
    async (item: IChequeListItem, index: number) => {
      dispatch(removeChequeItem({ itemId: item._id }));

      try {
        await dispatch(deleteCheque({ item, index })).unwrap();

        fetchPaginatedCheques();
        enqueueSnackbar('Cheque deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedCheques]
  );

  useEffect(() => {
    fetchPaginatedCheques();
    fetchChequeReceived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedCheques, fetchChequeReceived]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Cheques"
        links={[]}
        action={
          <Stack direction="row" gap={2}>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.DOWNLOAD_CHEQUE_REPORT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip title="Download Cheque Report">
                <Button variant="outlined" color="primary" onClick={downloadChequeReport}>
                  <Iconify icon="eva:arrow-circle-down-fill" width={24} />
                </Button>
              </Tooltip>
            )}

            {statusTab === 'all' && (
              <ListItemText
                primary="Pending Withdrawal"
                secondary={fCurrencyRupees(chequeTotal?.pendingWithdrawal ?? 0)}
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            )}

            <ListItemText
              primary="Total Value"
              secondary={fCurrencyRupees(chequeTotal?.totalAmount ?? 0)}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
              }}
            />

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_CHEQUE,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.cheque.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Cheque
              </Button>
            )}
          </Stack>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <Tabs
          value={statusTab}
          onChange={handleFilterStatus}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {STATUS_OPTIONS.map((tab) => (
            <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <ChequeTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
          activeFilterCount={activeFilterCount}
          received={receivedPeople}
          tableStatus={statusTab}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={activeSort.order}
                orderBy={activeSort.sortBy}
                headLabel={getDynamicTableHead(filters, isPaidTab, isBankingTab).filter(
                  ({ primary }) => {
                    if (settings.themeLayout === 'vertical') {
                      return primary;
                    }
                    return true;
                  }
                )}
                rowCount={cheque.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === activeSort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {cheque?.items.map((row: IChequeListItem, index: number) => (
                      <ChequeTableRow
                        key={row._id}
                        row={row}
                        selected={table.selected.includes(row._id)}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                        hasPaidDateFilter={!!filters.paidDate}
                        showPaidDate={isPaidTab}
                        showBankedDate={isBankingTab}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && cheque.count === 0 && (
                  <TableNoData message="No cheques found" notFound={cheque.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          dense
          count={cheque.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
